import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Send, FileDown, RefreshCw, CalendarClock, Clock, Mail, CheckCircle2, XCircle, AlertTriangle, Moon, Sunrise, Sparkles } from "lucide-react";
import BackButton from "../components/BackButton";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

const WEEKDAYS = [
  { v: 0, l: "Lunedì" }, { v: 1, l: "Martedì" }, { v: 2, l: "Mercoledì" },
  { v: 3, l: "Giovedì" }, { v: 4, l: "Venerdì" }, { v: 5, l: "Sabato" }, { v: 6, l: "Domenica" },
];
const HOURS = Array.from({ length: 24 }, (_, i) => ({ v: i, l: `${i.toString().padStart(2, "0")}:00` }));
const MONTH_DAYS = [
  { v: 0, l: "Ultimo giorno del mese" },
  ...Array.from({ length: 28 }, (_, i) => ({ v: i + 1, l: `Giorno ${i + 1}` })),
];

function StatusBadge({ status }) {
  const map = {
    sent: { icon: CheckCircle2, label: "Inviato", color: "bg-emerald-100 text-emerald-800" },
    failed: { icon: XCircle, label: "Fallito", color: "bg-red-100 text-red-800" },
    retrying: { icon: RefreshCw, label: "In retry", color: "bg-amber-100 text-amber-800" },
    skipped: { icon: AlertTriangle, label: "Saltato", color: "bg-slate-100 text-slate-700" },
  };
  const m = map[status] || map.skipped;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.color}`} data-testid={`digest-log-status-${status}`}>
      <Icon size={12} />
      {m.label}
    </span>
  );
}

export default function NotificheDigest() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState({ weekly: false, monthly: false });

  async function loadAll() {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.digestGetSettings(), api.digestLogs({ limit: 30 })]);
      setSettings(s);
      setLogs(l || []);
    } catch (e) {
      toast.error("Impossibile caricare le impostazioni digest");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function save(patch) {
    setSaving(true);
    try {
      const res = await api.digestUpdateSettings(patch);
      setSettings(res);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  }

  async function runNow(kind) {
    setSending((s) => ({ ...s, [kind]: true }));
    try {
      const res = await api.digestRunNow(kind);
      if (res.ok) {
        toast.success(`Digest ${kind === "weekly" ? "settimanale" : "mensile"} inviato a ${res.recipient_email}`);
      } else {
        toast.error(`Invio fallito: ${res.error || res.status}`);
      }
      // Refresh logs
      const l = await api.digestLogs({ limit: 30 });
      setLogs(l || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio digest");
    } finally {
      setSending((s) => ({ ...s, [kind]: false }));
    }
  }

  if (loading || !settings) {
    return <div className="pxos-card p-8 text-center opacity-70" data-testid="digest-loading">Caricamento…</div>;
  }

  return (
    <div className="space-y-6" data-testid="digest-page">
      <BackButton to="/settings" testid="digest-back-btn" />
      <div>
        <div className="flex items-center gap-2">
          <Bell size={22} className="opacity-70" />
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Notifiche digest</h1>
        </div>
        <p className="mt-2 text-sm opacity-80 max-w-2xl">
          Ricevi via email un PDF con il riepilogo della tua settimana e del tuo mese. La Segretaria IA prepara retrospettiva, prospettiva e attenzioni.
        </p>
      </div>

      <EndOfDayCard />

      {/* Blocco 2 · Fase 2c — Recap serale "Cosa hai domani" */}
      <TomorrowRecapCard />

      {/* Blocco 2 · Fase 2b² — Weekly Digest v2 (LLM) */}
      <WeeklyDigestV2Card />

      {/* ── Weekly card ─────────────────────────────────── */}
      <div className="pxos-card p-6" data-testid="digest-weekly-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarClock size={20} className="opacity-70" />
            <h2 className="font-display text-xl font-semibold">Digest settimanale</h2>
          </div>
          <Switch
            checked={settings.weekly_enabled}
            onCheckedChange={(v) => save({ weekly_enabled: v })}
            disabled={saving}
            data-testid="digest-weekly-toggle"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs opacity-70">Giorno</Label>
            <Select value={String(settings.weekly_weekday)}
                    onValueChange={(v) => save({ weekly_weekday: Number(v) })}>
              <SelectTrigger data-testid="digest-weekly-weekday"><SelectValue /></SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs opacity-70">Ora (Europe/Rome)</Label>
            <Select value={String(settings.weekly_hour)}
                    onValueChange={(v) => save({ weekly_hour: Number(v) })}>
              <SelectTrigger data-testid="digest-weekly-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map((h) => <SelectItem key={h.v} value={String(h.v)}>{h.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="default" onClick={() => runNow("weekly")}
                  disabled={sending.weekly}
                  data-testid="digest-weekly-send-now">
            <Send size={14} className="mr-1" />
            {sending.weekly ? "Invio in corso…" : "Invia ora"}
          </Button>
          <a href={api.digestPreviewUrl("weekly")} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="digest-weekly-preview">
              <FileDown size={14} className="mr-1" /> Anteprima PDF
            </Button>
          </a>
        </div>
      </div>

      {/* ── Monthly card ────────────────────────────────── */}
      <div className="pxos-card p-6" data-testid="digest-monthly-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock size={20} className="opacity-70" />
            <h2 className="font-display text-xl font-semibold">Digest mensile</h2>
          </div>
          <Switch
            checked={settings.monthly_enabled}
            onCheckedChange={(v) => save({ monthly_enabled: v })}
            disabled={saving}
            data-testid="digest-monthly-toggle"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs opacity-70">Giorno del mese</Label>
            <Select value={String(settings.monthly_day_of_month)}
                    onValueChange={(v) => save({ monthly_day_of_month: Number(v) })}>
              <SelectTrigger data-testid="digest-monthly-dom"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {MONTH_DAYS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs opacity-70">Ora (Europe/Rome)</Label>
            <Select value={String(settings.monthly_hour)}
                    onValueChange={(v) => save({ monthly_hour: Number(v) })}>
              <SelectTrigger data-testid="digest-monthly-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map((h) => <SelectItem key={h.v} value={String(h.v)}>{h.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="default" onClick={() => runNow("monthly")}
                  disabled={sending.monthly}
                  data-testid="digest-monthly-send-now">
            <Send size={14} className="mr-1" />
            {sending.monthly ? "Invio in corso…" : "Invia ora"}
          </Button>
          <a href={api.digestPreviewUrl("monthly")} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="digest-monthly-preview">
              <FileDown size={14} className="mr-1" /> Anteprima PDF
            </Button>
          </a>
        </div>
      </div>

      {/* ── Destinatario ───────────────────────────────── */}
      <div className="pxos-card p-6" data-testid="digest-recipient-card">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className="opacity-70" />
          <h2 className="font-display text-lg font-semibold">Destinatario</h2>
        </div>
        <Label className="text-xs opacity-70">Indirizzo email</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={settings.recipient_email || ""}
            onChange={(e) => setSettings({ ...settings, recipient_email: e.target.value })}
            placeholder="tua-email@esempio.it"
            className="flex-1"
            data-testid="digest-recipient-input"
          />
          <Button variant="default"
                  onClick={() => save({ recipient_email: settings.recipient_email || null })}
                  disabled={saving}
                  data-testid="digest-recipient-save">
            Salva
          </Button>
        </div>
        <p className="text-xs opacity-60 mt-2">
          Se lasciato vuoto, i digest verranno inviati all'email dell'account owner.
        </p>
      </div>

      {/* ── Logs ───────────────────────────────────────── */}
      <div className="pxos-card p-6" data-testid="digest-logs-card">        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <RefreshCw size={18} className="opacity-70" />
            <h2 className="font-display text-lg font-semibold">Cronologia invii</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={loadAll} data-testid="digest-logs-refresh">
            Aggiorna
          </Button>
        </div>
        {logs.length === 0 ? (
          <div className="text-sm opacity-70 text-center py-6" data-testid="digest-logs-empty">
            Nessun invio ancora registrato.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="digest-logs-list">
            {logs.map((l) => (
              <li key={l.id}
                  className="rounded-lg border p-3 flex flex-wrap items-center justify-between gap-2"
                  style={{ borderColor: "var(--pxos-border)" }}
                  data-testid={`digest-log-${l.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline">{l.kind === "weekly" ? "Settimanale" : "Mensile"}</Badge>
                  <StatusBadge status={l.status} />
                  {l.manual_trigger && <Badge variant="secondary">Manuale</Badge>}
                  <span className="text-sm">
                    {l.period_start} → {l.period_end}
                  </span>
                </div>
                <div className="text-xs opacity-70">
                  {l.recipient_email} · via {l.provider || "—"} · {l.sent_at ? new Date(l.sent_at).toLocaleString("it-IT") : new Date(l.created_at).toLocaleString("it-IT")}
                </div>
                {l.error_message && (
                  <div className="text-xs text-red-600 w-full mt-1" data-testid={`digest-log-error-${l.id}`}>
                    Errore: {l.error_message}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Fase 2 · Notifica di fine giornata
// ═══════════════════════════════════════════════════════════
function EndOfDayCard() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await api.getEndOfDayPrefs();
        setPrefs(p);
      } catch {
        toast.error("Impossibile caricare le preferenze fine giornata");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save(patch) {
    setSaving(true);
    try {
      const res = await api.updateEndOfDayPrefs(patch);
      setPrefs(res);
      toast.success("Preferenza salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !prefs) {
    return (
      <div className="pxos-card p-6 opacity-70" data-testid="end-of-day-card-loading">
        Caricamento…
      </div>
    );
  }

  return (
    <div className="pxos-card p-6" data-testid="end-of-day-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Moon size={20} className="opacity-70" />
          <h2 className="font-display text-xl font-semibold">Notifica di fine giornata</h2>
        </div>
        <Switch
          checked={!!prefs.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
          disabled={saving}
          data-testid="end-of-day-toggle"
        />
      </div>
      <p className="text-sm opacity-80 max-w-2xl mb-4">
        Alla sera ti mando un promemoria per chiederti "come è andata la giornata?".
        Se ci sono cicli in agenda ancora aperti, puoi completarli tutti, rimandarli a
        domani o gestirli uno per uno. Ricevi email e notifica in-app.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
        <div>
          <Label className="text-xs opacity-70">Orario (Europe/Rome)</Label>
          <Input
            type="time"
            value={prefs.time || "21:00"}
            onChange={(e) => setPrefs({ ...prefs, time: e.target.value })}
            onBlur={(e) => {
              if (e.target.value && e.target.value !== prefs?.time) return;
            }}
            disabled={!prefs.enabled || saving}
            data-testid="end-of-day-time-input"
          />
          <p className="text-xs opacity-60 mt-1.5">Finestra di invio: ±7,5 minuti.</p>
        </div>
        <div className="flex items-end">
          <Button
            variant="default"
            onClick={() => save({ time: prefs.time })}
            disabled={saving || !prefs.enabled}
            data-testid="end-of-day-save-time-btn"
          >
            Salva orario
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Blocco 2 · Fase 2c · Recap serale "Cosa hai domani"
// ═══════════════════════════════════════════════════════════
function TomorrowRecapCard() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPrefs(await api.getTomorrowRecapPrefs());
      } catch {
        toast.error("Impossibile caricare le preferenze recap serale");
      } finally { setLoading(false); }
    })();
  }, []);

  async function save(patch) {
    setSaving(true);
    try {
      const res = await api.updateTomorrowRecapPrefs(patch);
      setPrefs(res);
      toast.success("Preferenza salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  }

  if (loading || !prefs) {
    return <div className="pxos-card p-6 opacity-70" data-testid="tomorrow-recap-card-loading">Caricamento…</div>;
  }

  return (
    <div className="pxos-card p-6" data-testid="tomorrow-recap-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sunrise size={20} className="opacity-70" />
          <h2 className="font-display text-xl font-semibold">Cosa hai domani</h2>
        </div>
        <Switch
          checked={!!prefs.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
          disabled={saving}
          data-testid="tomorrow-recap-toggle"
        />
      </div>
      <p className="text-sm opacity-80 max-w-2xl mb-4">
        Alla sera ti mando via email e in-app l'elenco dei cicli programmati per domani più le tue azioni della settimana in corso.
        La domenica sera viene sospeso in favore del riepilogo settimanale.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
        <div>
          <Label className="text-xs opacity-70">Orario (Europe/Rome)</Label>
          <Input
            type="time"
            value={prefs.time || "20:00"}
            onChange={(e) => setPrefs({ ...prefs, time: e.target.value })}
            disabled={!prefs.enabled || saving}
            data-testid="tomorrow-recap-time-input"
          />
          <p className="text-xs opacity-60 mt-1.5">Finestra di invio: ±7,5 minuti.</p>
        </div>
        <div className="flex items-end">
          <Button
            variant="default"
            onClick={() => save({ time: prefs.time })}
            disabled={saving || !prefs.enabled}
            data-testid="tomorrow-recap-save-time-btn"
          >
            Salva orario
          </Button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// Blocco 2 · Fase 2b² · Weekly Digest v2 (Claude Sonnet 4.5)
// ═══════════════════════════════════════════════════════════
function WeeklyDigestV2Card() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPrefs(await api.getWeeklyDigestPrefs());
      } catch {
        toast.error("Impossibile caricare le preferenze riepilogo domenicale");
      } finally { setLoading(false); }
    })();
  }, []);

  async function save(patch) {
    setSaving(true);
    try {
      const res = await api.updateWeeklyDigestPrefs(patch);
      setPrefs(res);
      toast.success("Preferenza salvata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  }

  if (loading || !prefs) {
    return <div className="pxos-card p-6 opacity-70" data-testid="weekly-digest-v2-card-loading">Caricamento…</div>;
  }

  return (
    <div className="pxos-card p-6" data-testid="weekly-digest-v2-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="opacity-70" />
          <h2 className="font-display text-xl font-semibold">Riepilogo domenicale</h2>
        </div>
        <Switch
          checked={!!prefs.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
          disabled={saving}
          data-testid="weekly-digest-v2-toggle"
        />
      </div>
      <p className="text-sm opacity-80 max-w-2xl mb-4">
        Ogni domenica sera ricevi il riassunto della settimana appena conclusa, uno sguardo alla settimana entrante, un
        suggerimento pratico e una frase motivazionale generati dalla Segretaria (Claude Sonnet 4.5) partendo
        <strong> solo</strong> dai tuoi dati reali (mete, azioni, cicli). Nessuna informazione inventata.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
        <div>
          <Label className="text-xs opacity-70">Orario (Europe/Rome, domenica)</Label>
          <Input
            type="time"
            value={prefs.time || "20:00"}
            onChange={(e) => setPrefs({ ...prefs, time: e.target.value })}
            disabled={!prefs.enabled || saving}
            data-testid="weekly-digest-v2-time-input"
          />
          <p className="text-xs opacity-60 mt-1.5">Finestra di invio: ±7,5 minuti.</p>
        </div>
        <div className="flex items-end">
          <Button
            variant="default"
            onClick={() => save({ time: prefs.time })}
            disabled={saving || !prefs.enabled}
            data-testid="weekly-digest-v2-save-time-btn"
          >
            Salva orario
          </Button>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between rounded-lg border p-3" style={{ borderColor: "var(--pxos-border)" }}>
        <div>
          <div className="text-sm font-medium">Digest PDF settimanale (legacy)</div>
          <div className="text-xs opacity-70 mt-0.5">
            Il vecchio digest PDF è disattivato di default. Riattivalo se lo desideri.
          </div>
        </div>
        <Switch
          checked={!!prefs.pdf_enabled}
          onCheckedChange={(v) => save({ pdf_enabled: v })}
          disabled={saving}
          data-testid="weekly-digest-v2-pdf-toggle"
        />
      </div>
    </div>
  );
}

